/* global $, SGEvent */
import SGLink from './shopgate-link';
import SGActionCommand from '../app/sg-actions/sg-action-command';

class LinkSanitizer {
  static get nonNavigationProtocols() {
    return [
      'mailto',
      'tel',
    ];
  }

  /**
   * Checks if the passed url is an external url
   * @param  {string}  url The url
   * @return {boolean} TRUE if is is an external url. FALSE if not
   */
  static isExternalLink(url) {
    return url.indexOf(':') !== -1;
  }

  /**
   * Checks if url is something like mailto: which should be ignored.
   * @param {string} url url
   * @return {boolean}
   */
  static isNonNavigationLink(url) {
    let found = false;
    this.nonNavigationProtocols.some((p) => {
      found = url.indexOf(p) === 0;
      return found;
    });
    return found;
  }

  static makeOptions(o) {
    const defaults = {
      cname: '',
      alternativeRegexAlias: '',
      hybrid: false,
    };
    return $.extend(true, {}, defaults, o);
  }

  constructor(o) {
    this.options = this.constructor.makeOptions(o);
    // Only remove leading slash for hybrid apps
    this.regexAlias =
      new RegExp(
        `^(https?://)?[^.]+(.shopgate(pg)?.com)${(this.options.hybrid ? '/?' : '')}`,
        'i'
      );
    // Only remove leading slash for hybrid apps
    this.regexCName =
      new RegExp(
        `^(https?://)?${this.options.cname}${(this.options.hybrid ? '/?' : '')}`,
        'i'
      );
    this.regexAlternativeAlias = this.options.alternativeRegexAlias ?
      new RegExp(this.options.alternativeRegexAlias, 'i')
      : null;
  }

  /**
   * Checks if the passed url is a shopgate alias
   * @param  {string}  url The url
   * @return {boolean} TRUE if is is an alias. FALSE if not
   */
  isAliasLink(url) {
    if (typeof url !== 'string') {
      return false;
    }

    let result = this.regexAlias.test(url);

    if (!result && this.options.alternativeRegexAlias) {
      result = this.regexAlternativeAlias.test(url);
    }

    return result;
  }

  /**
   * Checks if the passed url is a shop cname
   * @param  {string}  url The url
   * @return {bool} TRUE if is is a CNAME. FALSE if not
   */
  isCNameLink(url) {
    const result =
            typeof url === 'string'
            && this.options.cname ?
              this.regexCName.test(url)
              : false;
    return result;
  }

  /**
   * Resets regex
   */
  resetRegex() {
    // reset the regular expression objects
    this.regexAlias.lastIndex = 0;
    this.regexCName.lastIndex = 0;

    if (this.regexAlternativeAlias) {
      this.regexAlternativeAlias.lastIndex = 0;
    }
  }

  /**
   * Sanitizes element to be safely use in widgets and other user reachable places.
   * @param {HtmlElement} el element
   */
  sanitizeJQueryElement(el) {
    this.resetRegex();

    const linkElement = $(el);
    let link = linkElement.attr('href');
    if (link === 'sgapi:') {
      link = '/';
    }

    if (linkElement.attr('target') === '_blank') {
      /*
       * Remove sgcommands, to avoid return early.
       * If target=_blank then sgcommands which are made by server are wrong.
       * There is a TD to remove server side implementation completetly.
       * This is a hack to force SGLink to handle those links.
      */
      linkElement.removeAttr('data-sgcommands');
      linkElement.data('sgcommands', null);
    }

    /**
     * If href is empty (href="") it should not be
     * processed within the app.
     */
    if (!link) {
      linkElement.removeAttr('href');
      return;
    }

    const sgLink = new SGLink(link);

    this.sanitizeCouponLinks(linkElement, sgLink);

    // Do not process the element if it doesn't contain a link or have already commands applied
    if (linkElement.data('sgcommands')) {
      return;
    }

    // No need to touch things like mailto:
    if (this.constructor.isNonNavigationLink(link)) {
      return;
    }

    // Check the link type
    const isAlias = this.isAliasLink(link);
    const isCName = this.isCNameLink(link);
    const isExternal = !isAlias && !isCName ? this.constructor.isExternalLink(link) : false;

    // Create commands and special links for the hybrid apps
    if (this.options.hybrid) {
      if (!linkElement.hasClass('js-ajax-link')) {
        // Remove the href if the link shall not be opened via ajax
        linkElement.removeAttr('href');
      }

      if (isExternal) {
        let commandName = 'openPage';
        if (linkElement.attr('target') === '_blank') {
          commandName = 'openPageExtern';
        }
        const cmds = [
          new SGActionCommand(commandName, {
            src: sgLink.toString(),
          }),
        ];
        linkElement
          .attr(
            'data-sgcommands',
            JSON.stringify(cmds)
          )
          .data('sgcommands', cmds);
        return;
      }
      const cmds = sgLink.toCommands(linkElement.attr('title'));
      linkElement
        .attr(
          'data-sgcommands',
          JSON.stringify(cmds)
        )
        .data('sgcommands', cmds);
      return;
    }

    if (isExternal) {
      linkElement.attr('href', sgLink.toString());
      return;
    }
    linkElement.attr('href', sgLink.toRelativeString());
  }

  /**
   * binds ajax calls to the
   * @param {jQuery} el - link element
   * @param {SGLink} link - sglink object
   */
  sanitizeCouponLinks(el, link) {
    // For cart_add_coupon urls we let the linke be processed via ajax
    if (link.url.indexOf('cart_add_coupon') !== -1) {
      $(el).addClass('no-sg-action js-ajax-link');
      return;
    }

    if (!this.options.hybrid) {
      return;
    }

    const couponCode = link.getParam('coupon');

    // We only process 'link?coupon=foo' urls via ajax if no openPage will happen (isRootPage)
    if (typeof couponCode === 'undefined' || !link.isRootPage()) {
      return;
    }

    $(el).bind('click', function () {
      $.ajax({
        url: `sgapi:cart_add_coupon/${couponCode}`,
        method: 'POST',
        success: (response) => {
          SGEvent.Broadcast.universalEvent({
            eventName: 'showFlashMessage',
            eventParams: response.systemMessage,
            receiver: 'active',
          });
        },
      });
    });
  }
}

export default LinkSanitizer;



// WEBPACK FOOTER //
// ./Library/1.0/common/link-sanitizer.js